// What the button on each plan card does for the person looking at it. Pure,
// so every viewer state has a test (planCta.test.ts) — this is the logic that
// used to be "paid users only ever see Manage Subscription".
import type { UserPlan } from "@/lib/auth/plans";
import type { SubscriptionStatus } from "@/lib/auth/subscription";
import {
  PLAN_CONFIG,
  PLAN_RANK,
  isPlanForSale,
  planRank,
  type BillingInterval,
  type PaidPlan,
} from "@/lib/billing/planConfig";

/** Statuses in which a Creem subscription exists and checkout must refuse. */
export const LIVE_SUBSCRIPTION_STATUSES: ReadonlySet<SubscriptionStatus> = new Set([
  "active",
  "trialing",
  "past_due",
  "paused",
  "scheduled_cancel",
]);

function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "the period end"
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export type PlanViewer = {
  plan: UserPlan;
  status: SubscriptionStatus | null;
  /** Paid with no Creem subscription behind it (comped). */
  complimentary: boolean;
  /** What Creem bills — known once the billing state has loaded. */
  billed: { plan: PaidPlan; interval: BillingInterval } | null;
  /** A scheduled downgrade. */
  pending: { plan: PaidPlan; interval: BillingInterval; effectiveAt: string | null } | null;
  /** When a scheduled cancellation takes effect. */
  endsAt: string | null;
};

export type CardAction =
  | { kind: "signup"; label: string }
  | { kind: "checkout"; plan: PaidPlan; interval: BillingInterval; label: string }
  | {
      kind: "change";
      plan: PaidPlan;
      interval: BillingInterval;
      direction: "upgrade" | "downgrade" | "undo";
      label: string;
    }
  | { kind: "cancel"; label: string }
  | { kind: "resume"; label: string }
  | { kind: "fix_payment"; label: string }
  | { kind: "disabled"; label: string; tone: "current" | "muted" }
  /**
   * The checkout or change this card would offer, for a plan that isn't on
   * sale yet: a disabled button with a "Coming soon" popover and no handler.
   */
  | { kind: "coming_soon"; plan: PaidPlan; label: string };

export function hasLiveSubscription(viewer: Pick<PlanViewer, "status" | "complimentary">): boolean {
  return !viewer.complimentary && viewer.status !== null && LIVE_SUBSCRIPTION_STATUSES.has(viewer.status);
}

/** The viewer has this plan now, or Creem bills them for it. */
export function holdsPlan(viewer: Pick<PlanViewer, "plan" | "billed">, plan: PaidPlan): boolean {
  return viewer.plan === plan || viewer.billed?.plan === plan;
}

/**
 * The viewer can't move onto `plan` because it isn't on sale yet. Whoever
 * already holds it keeps managing it (yearly, undo a downgrade); the server
 * makes the same call in /api/checkout and /api/billing/change-plan.
 */
export function comingSoonFor(
  viewer: Pick<PlanViewer, "plan" | "billed">,
  plan: PaidPlan,
  forSale: (plan: PaidPlan) => boolean = isPlanForSale,
): boolean {
  return !forSale(plan) && !holdsPlan(viewer, plan);
}

/**
 * @param card      the plan this card sells ("free" for the free tier)
 * @param interval  the billing period the toggle shows
 * @param forSale   which plans are on sale (tests pass their own)
 */
export function planCardAction(
  viewer: PlanViewer,
  card: "free" | PaidPlan,
  interval: BillingInterval,
  forSale: (plan: PaidPlan) => boolean = isPlanForSale,
): CardAction {
  const action = onSaleCardAction(viewer, card, interval);
  if ((action.kind === "checkout" || action.kind === "change") && comingSoonFor(viewer, action.plan, forSale)) {
    return { kind: "coming_soon", plan: action.plan, label: action.label };
  }
  return action;
}

/** The card's action when every plan is on sale. */
function onSaleCardAction(
  viewer: PlanViewer,
  card: "free" | PaidPlan,
  interval: BillingInterval,
): CardAction {
  const label = card === "free" ? "Free" : PLAN_CONFIG[card].label;

  // Signed out: everything starts with an account.
  if (viewer.plan === "anonymous") {
    return card === "free"
      ? { kind: "signup", label: "Create free account" }
      : { kind: "checkout", plan: card, interval, label: `Get ${label}` };
  }

  // Comped by hand — nothing to buy or manage. A comped tier includes the
  // ones below it, not the ones above (a comped Pro has no API keys).
  if (viewer.complimentary) {
    if (card === viewer.plan) return { kind: "disabled", label: "Complimentary", tone: "current" };
    return planRank(card) <= planRank(viewer.plan)
      ? { kind: "disabled", label: "Included", tone: "muted" }
      : { kind: "disabled", label: "Not included", tone: "muted" };
  }

  const live = hasLiveSubscription(viewer);

  // Free account, or a subscription that has ended: a fresh checkout.
  if (!live) {
    if (card === "free") {
      return viewer.plan === "free"
        ? { kind: "disabled", label: "Current plan", tone: "current" }
        : { kind: "disabled", label: "Free", tone: "muted" };
    }
    return { kind: "checkout", plan: card, interval, label: `Upgrade to ${label}` };
  }

  // A subscription exists. Its own card carries the urgent state first.
  const entitled = viewer.plan === "pro" || viewer.plan === "studio" ? viewer.plan : (viewer.billed?.plan ?? null);
  const isOwnCard = card !== "free" && card === entitled;

  if (viewer.status === "past_due") {
    return isOwnCard
      ? { kind: "fix_payment", label: "Update payment method" }
      : { kind: "disabled", label: "Fix payment first", tone: "muted" };
  }
  if (viewer.status === "scheduled_cancel" || viewer.status === "paused") {
    if (isOwnCard) return { kind: "resume", label: "Resume subscription" };
    if (card === "free") {
      return { kind: "disabled", label: viewer.endsAt ? `From ${shortDate(viewer.endsAt)}` : "After this period", tone: "muted" };
    }
    return { kind: "disabled", label: "Resume to switch", tone: "muted" };
  }

  if (card === "free") return { kind: "cancel", label: "Switch to Free" };

  const billed = viewer.billed;
  const pending = viewer.pending;

  if (pending) {
    if (card === pending.plan) {
      return { kind: "disabled", label: "Scheduled", tone: "muted" };
    }
    if (card === entitled) {
      return {
        kind: "change",
        plan: card,
        interval: pending.interval,
        direction: "undo",
        label: `Keep ${label}`,
      };
    }
  }

  if (isOwnCard) {
    // Same tier: the only move left is monthly → yearly.
    if (billed && billed.interval === "month" && interval === "year") {
      return { kind: "change", plan: card, interval: "year", direction: "upgrade", label: "Switch to yearly" };
    }
    if (billed && billed.interval === "year" && interval === "month") {
      return { kind: "disabled", label: "Billed yearly", tone: "current" };
    }
    return { kind: "disabled", label: "Current plan", tone: "current" };
  }

  // A different paid tier, on the interval the user is already billed on —
  // plan and interval change one at a time. When the toggle shows the other
  // interval, the label says which one the button actually buys.
  const changeInterval = billed?.interval ?? interval;
  const qualifier = changeInterval === interval ? "" : changeInterval === "month" ? " (monthly)" : " (yearly)";
  if (entitled && PLAN_RANK[card] > PLAN_RANK[entitled]) {
    return {
      kind: "change",
      plan: card,
      interval: changeInterval,
      direction: "upgrade",
      label: `Upgrade to ${label}${qualifier}`,
    };
  }
  return {
    kind: "change",
    plan: card,
    interval: changeInterval,
    direction: "downgrade",
    label: `Switch to ${label}${qualifier}`,
  };
}
